// ContextMenu.jsx

import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ContextMenu.css';

// Icons
import Icon from 'svg/Icon';

// Utilities
import { hexToRgba } from 'utilities/Colors';

// Components
import ReactDOM from 'react-dom';

const MENU_VIEWPORT_PADDING = 8;
const SUBMENU_OVERLAP_PX = 4;

const ContextMenuSurfaceContext = React.createContext(null);

const normalizeAnchorPoint = (anchorPoint) => {
    const x = Number(anchorPoint?.x);
    const y = Number(anchorPoint?.y);
    return {
        x: Number.isFinite(x) ? x : 0,
        y: Number.isFinite(y) ? y : 0,
    };
};

const pathStartsWith = (path, prefix) => {
    if (!Array.isArray(path) || !Array.isArray(prefix)) return false;
    if (path.length < prefix.length) return false;
    return prefix.every((value, index) => path[index] === value);
};

const pathsEqual = (left, right) => {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
};

const ContextMenuItem = () => null;
ContextMenuItem.displayName = 'ContextMenuItem';

const ContextMenuSection = () => null;
ContextMenuSection.displayName = 'ContextMenuSection';

const ContextMenuSeparator = () => null;
ContextMenuSeparator.displayName = 'ContextMenuSeparator';

/**
 * Flatten Fragments so conditional item groups still register as menu children.
 * ContextMenu only accepts Item / Section / Separator at the menu surface.
 *
 * @param {ReactNode} children
 * @returns {ReactElement[]}
 */
const flattenMenuNodes = (children) => {
    const nodes = [];
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        if (child.type === React.Fragment) {
            nodes.push(...flattenMenuNodes(child.props.children));
            return;
        }
        nodes.push(child);
    });
    return nodes;
};

const getMenuChildren = (children) => (
    flattenMenuNodes(children).filter((child) => (
        child.type === ContextMenuItem
        || child.type === ContextMenuSection
        || child.type === ContextMenuSeparator
    ))
);

// Submenus may include Section / Separator (e.g. nested "Add related…" contents).
const getChildItems = (children) => (
    flattenMenuNodes(children).filter((child) => (
        child.type === ContextMenuItem
        || child.type === ContextMenuSection
        || child.type === ContextMenuSeparator
    ))
);

const getPortalRoot = () => document.getElementById('portal-root') || document.body;

/**
 * Positions a submenu inside the viewport (flip left / clamp vertically).
 * Portals outside the root menu so backdrop-filter can blur the page.
 *
 * @param {object} props
 * @param {ReactNode} props.children - Submenu level contents.
 * @returns {ReactNode}
 */
const ContextMenuSubmenuWrap = ({ children }) => {
    const surfaceContext = useContext(ContextMenuSurfaceContext);
    const anchorRef = useRef(null);
    const wrapRef = useRef(null);
    const [placement, setPlacement] = useState(null);

    useLayoutEffect(() => {
        const wrap = wrapRef.current;
        const item = anchorRef.current?.parentElement;
        if (!wrap || !item) return undefined;

        const updatePlacement = () => {
            const itemRect = item.getBoundingClientRect();
            const submenuWidth = wrap.offsetWidth;
            const submenuHeight = wrap.offsetHeight;
            const maxRight = window.innerWidth - MENU_VIEWPORT_PADDING;
            const maxBottom = window.innerHeight - MENU_VIEWPORT_PADDING;
            const minTop = MENU_VIEWPORT_PADDING;

            const spaceRight = maxRight - itemRect.right;
            const openLeft = submenuWidth > spaceRight;

            let top = itemRect.top - 6;
            if (top + submenuHeight > maxBottom) {
                top = maxBottom - submenuHeight;
            }
            if (top < minTop) {
                top = minTop;
            }

            const left = openLeft
                ? itemRect.left - submenuWidth + SUBMENU_OVERLAP_PX
                : itemRect.right - SUBMENU_OVERLAP_PX;

            setPlacement({ left, top });
        };

        updatePlacement();
        window.addEventListener('resize', updatePlacement);
        // Keep submenus aligned when the root menu (or any ancestor) scrolls.
        window.addEventListener('scroll', updatePlacement, true);
        return () => {
            window.removeEventListener('resize', updatePlacement);
            window.removeEventListener('scroll', updatePlacement, true);
        };
    }, []);

    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap || typeof surfaceContext?.registerSurface !== 'function') return undefined;
        return surfaceContext.registerSurface(wrap);
    }, [surfaceContext, placement]);

    /**
     * Collapses submenu when the pointer leaves all menu surfaces.
     *
     * @param {MouseEvent} event
     */
    const handleMouseLeave = (event) => {
        if (surfaceContext?.isInsideMenu?.(event.relatedTarget)) return;
        surfaceContext?.clearHover?.();
    };

    const surfaceStyle = {
        ...(surfaceContext?.surfaceStyle || {}),
        left: placement?.left ?? 0,
        top: placement?.top ?? 0,
        visibility: placement ? 'visible' : 'hidden',
        zIndex: (surfaceContext?.zIndex || 1200) + 1,
    };

    return (
        <>
            <span
              aria-hidden="true"
              className="context-menu-submenu-anchor"
              ref={anchorRef}
            />
            {ReactDOM.createPortal(
                <div
                  className="context-menu-submenu-wrap"
                  ref={wrapRef}
                  style={surfaceStyle}
                  onMouseLeave={handleMouseLeave}
                >
                    {children}
                </div>,
                getPortalRoot(),
            )}
        </>
    );
};

const ContextMenuLevel = ({
    items,
    level = 0,
    path = [],
    hoveredPath,
    setHoveredPath,
    onItemSelect,
    rootLevel,
    theme,
}) => (
    <div className={`context-menu-level ${rootLevel ? 'root' : 'submenu'}`}>
        {items.map((child, index) => {
            if (child.type === ContextMenuSection) {
                const label = String(child.props?.label || '').trim();
                if (!label) return null;
                return (
                    <div
                        className="context-menu-section-label"
                        key={`context_menu_section_${level}_${index}_${label}`}
                    >
                        {label}
                    </div>
                );
            }

            if (child.type === ContextMenuSeparator) {
                return (
                    <div
                        className="context-menu-separator"
                        key={`context_menu_sep_${level}_${index}`}
                    />
                );
            }

            const childPath = [...path, index];
            const childProps = child.props || {};
            const childItems = getChildItems(childProps.children);
            const hasChildren = childItems.length > 0;
            const isExpanded = hasChildren && pathStartsWith(hoveredPath, childPath);
            const isDisabled = childProps.disabled === true;
            const isHovered = !isDisabled && pathsEqual(hoveredPath, childPath);

            return (
                <div
                    className={[
                        'context-menu-item',
                        isHovered ? 'context-menu-item--hovered' : '',
                        isDisabled ? 'context-menu-item--disabled' : '',
                    ].filter(Boolean).join(' ')}
                    key={`context_menu_${level}_${index}_${childProps.label || 'item'}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (isDisabled) return;
                        if (hasChildren) return;
                        if (typeof childProps.onClick === 'function') {
                            childProps.onClick(event);
                        }
                        onItemSelect();
                    }}
                    onMouseEnter={() => {
                        if (!isDisabled) setHoveredPath(childPath);
                    }}
                >
                    <div className="context-menu-item-main">
                        {childProps.iconResource || childProps.iconKey ? (
                            <div className="context-menu-item-icon">
                                {childProps.iconResource ? (
                                    <img
                                      alt=""
                                      className="context-menu-item-icon-resource"
                                      draggable={false}
                                      src={childProps.iconResource}
                                    />
                                ) : (
                                    <Icon
                                      iconKey={childProps.iconKey}
                                      color={theme.foregroundMuted}
                                      width={15}
                                      height={15}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="context-menu-item-icon placeholder" />
                        )}

                        <div className="context-menu-item-label">
                            {childProps.label}
                        </div>
                    </div>

                    {hasChildren ? (
                        <div className="context-menu-item-arrow">
                            <Icon
                                iconKey="chevron_forward"
                                color={theme.foregroundMuted}
                                width={14}
                                height={14}
                            />
                        </div>
                    ) : null}

                    {hasChildren && isExpanded ? (
                        <ContextMenuSubmenuWrap>
                            <ContextMenuLevel
                                items={childItems}
                                level={level + 1}
                                path={childPath}
                                hoveredPath={hoveredPath}
                                setHoveredPath={setHoveredPath}
                                rootLevel={false}
                                theme={theme}
                                onItemSelect={onItemSelect}
                            />
                        </ContextMenuSubmenuWrap>
                    ) : null}
                </div>
            );
        })}
    </div>
);

/**
 * ContextMenu
 *
 * Renders a desktop-style context menu at a mouse anchor point, with support for nested submenus.
 *
 * @param {boolean} open - Whether the menu should be visible.
 * @param {function} setOpen - Function to update open state.
 * @param {{x: number, y: number}} anchorPoint - Screen coordinates used to position the root menu.
 * @param {object} triggerRef - Ref to the element that triggers the menu.
 * @param {string} triggerMode - Trigger type: contextmenu or click.
 * @param {ReactNode} children - ContextMenu.Item / Section / Separator elements.
 *   Item props: label, iconKey, iconResource, onClick, disabled, children (submenu).
 * @param {number} zIndex - Optional z-index for the menu surface.
 *
 * @returns {ReactNode} The rendered context menu.
 */
const ContextMenu = React.memo(({
    open,
    setOpen,
    anchorPoint,
    triggerRef,
    triggerMode = 'contextmenu',
    children,
    zIndex = 1200,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [internalOpen, setInternalOpen] = useState(false);
    const [internalAnchorPoint, setInternalAnchorPoint] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState(() => normalizeAnchorPoint(anchorPoint));
    const [hoveredPath, setHoveredPath] = useState([]);

    // ----- REFS ----- //

    const containerRef = useRef(null);
    const surfacesRef = useRef(new Set());

    const isControlled = typeof open === 'boolean';
    const resolvedOpen = isControlled ? open : internalOpen;
    const resolvedAnchorPoint = anchorPoint || internalAnchorPoint;

    // ----- MEMOS ----- //

    const rootItems = useMemo(() => getMenuChildren(children), [children]);

    const surfaceStyle = useMemo(() => ({
        '--context-menu-hover-bg': hexToRgba(theme.foreground, 0.1),
        '--context-menu-submenu-border': theme.status,
        borderColor: theme.status,
        boxShadow: '0 8px 30px #00000066',
    }), [theme.foreground, theme.status]);

    /**
     * Registers a menu surface (root or portaled submenu) for hit-testing.
     *
     * @param {HTMLElement|null} element
     * @returns {function|undefined} Unregister cleanup.
     */
    const registerSurface = useCallback((element) => {
        if (!element) return undefined;
        surfacesRef.current.add(element);
        return () => {
            surfacesRef.current.delete(element);
        };
    }, []);

    /**
     * Returns whether a DOM node is inside any registered menu surface.
     *
     * @param {EventTarget|null} target
     * @returns {boolean}
     */
    const isInsideMenu = useCallback((target) => {
        if (!(target instanceof Node)) return false;
        for (const surface of surfacesRef.current) {
            if (surface.contains(target)) return true;
        }
        return false;
    }, []);

    /**
     * Clears submenu hover state.
     */
    const clearHover = useCallback(() => {
        setHoveredPath([]);
    }, []);

    const surfaceContextValue = useMemo(() => ({
        clearHover,
        isInsideMenu,
        registerSurface,
        surfaceStyle,
        zIndex,
    }), [clearHover, isInsideMenu, registerSurface, surfaceStyle, zIndex]);

    /**
     * Updates open state for controlled and uncontrolled usage.
     *
     * @param {boolean} nextOpen - Next open state.
     */
    const setMenuOpen = useCallback((nextOpen) => {
        if (typeof setOpen === 'function') {
            setOpen(nextOpen);
        }
        if (!isControlled) {
            setInternalOpen(nextOpen);
        }
    }, [isControlled, setOpen]);

    /**
     * Closes the context menu.
     */
    const closeMenu = useCallback(() => {
        setMenuOpen(false);
    }, [setMenuOpen]);

    // ----- HANDLERS ----- //

    /**
     * Collapses submenus when the pointer leaves all menu surfaces.
     *
     * @param {MouseEvent} event
     */
    const handleMenuMouseLeave = (event) => {
        if (isInsideMenu(event.relatedTarget)) return;
        clearHover();
    };

    // ----- EFFECTS ----- //

    useEffect(() => {
        const triggerElement = triggerRef?.current;
        if (!triggerElement) return;
        if (rootItems.length === 0) return;

        const eventName = triggerMode === 'click' ? 'click' : 'contextmenu';

        const handleOpenFromTrigger = (event) => {
            if (eventName === 'contextmenu') {
                event.preventDefault();
            }

            const nextAnchorPoint = {
                x: event.clientX,
                y: event.clientY,
            };

            setInternalAnchorPoint(nextAnchorPoint);
            setMenuOpen(true);
        };

        triggerElement.addEventListener(eventName, handleOpenFromTrigger);
        return () => {
            triggerElement.removeEventListener(eventName, handleOpenFromTrigger);
        };
    }, [triggerRef, triggerMode, rootItems.length, setMenuOpen]);

    useEffect(() => {
        if (rootItems.length === 0) {
            closeMenu();
        }
    }, [rootItems.length, closeMenu]);

    useEffect(() => {
        if (!resolvedOpen) {
            setHoveredPath([]);
            surfacesRef.current.clear();
            return undefined;
        }

        const handlePointerDown = (event) => {
            if (isInsideMenu(event.target)) return;
            closeMenu();
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                closeMenu();
            }
        };

        window.addEventListener('mousedown', handlePointerDown);
        window.addEventListener('keydown', handleEscape);

        return () => {
            window.removeEventListener('mousedown', handlePointerDown);
            window.removeEventListener('keydown', handleEscape);
        };
    }, [resolvedOpen, closeMenu, isInsideMenu]);

    useEffect(() => {
        if (!resolvedOpen) return undefined;
        const container = containerRef.current;
        if (!container) return undefined;
        return registerSurface(container);
    }, [resolvedOpen, registerSurface, position]);

    useLayoutEffect(() => {
        if (!resolvedOpen) return;

        const nextAnchorPoint = normalizeAnchorPoint(resolvedAnchorPoint);
        setPosition(nextAnchorPoint);

        const containerElement = containerRef.current;
        if (!containerElement) return;

        const rect = containerElement.getBoundingClientRect();
        const maxLeft = Math.max(MENU_VIEWPORT_PADDING, window.innerWidth - rect.width - MENU_VIEWPORT_PADDING);
        const maxTop = Math.max(MENU_VIEWPORT_PADDING, window.innerHeight - rect.height - MENU_VIEWPORT_PADDING);

        setPosition({
            x: Math.min(Math.max(nextAnchorPoint.x, MENU_VIEWPORT_PADDING), maxLeft),
            y: Math.min(Math.max(nextAnchorPoint.y, MENU_VIEWPORT_PADDING), maxTop),
        });
    }, [resolvedOpen, resolvedAnchorPoint]);

    if (!resolvedOpen || rootItems.length === 0) {
        return null;
    }

    // ----- RENDER ----- //

    return ReactDOM.createPortal(
        <ContextMenuSurfaceContext.Provider value={surfaceContextValue}>
            <div
              className="context-menu-container"
              style={{
                ...surfaceStyle,
                left: position.x,
                top: position.y,
                zIndex,
              }}
              ref={containerRef}
              onMouseLeave={handleMenuMouseLeave}
            >
                <ContextMenuLevel
                  items={rootItems}
                  hoveredPath={hoveredPath}
                  setHoveredPath={setHoveredPath}
                  rootLevel
                  theme={theme}
                  onItemSelect={closeMenu}
                />
            </div>
        </ContextMenuSurfaceContext.Provider>,
        getPortalRoot(),
    );
});

ContextMenu.Item = ContextMenuItem;
ContextMenu.Section = ContextMenuSection;
ContextMenu.Separator = ContextMenuSeparator;

// Dev Tools
ContextMenu.displayName = 'ContextMenu';

export default ContextMenu;
